import { type SupplierJoinRequest } from '@choco-admin/types';

import { useSuppliersPageGetRequestsToJoinQuery } from '../../../../../../../generated';
import {
  countJoinRequestsInWindow,
  getJoinRequestsLoadError,
  mapSupplierJoinRequests,
} from '../../../../../utils/supplierJoinRequests';

// The connection exposes no pageInfo, so truncation is read off a full page.
// The resolver ignores `first` entirely and hardcodes its own cap, so this
// mirrors that number rather than choosing one. userService
// GetRequestsToJoinService.getManyBySupplierEntityId passes a literal 20 with
// the comment "currently only 20 requests are returned, remove once pagination
// is added".
const REQUESTS_HARD_CAP = 20;

export interface UseSupplierJoinRequestsResult {
  /** The pending requests inside the window the backend returned. */
  requests: SupplierJoinRequest[];
  /**
   * Whether the backend's window is full, so requests exist that this screen
   * cannot see. True with zero pending requests means "cannot tell", not
   * "nobody is waiting".
   */
  isTruncated: boolean;
  isLoading: boolean;
  /** CS-facing copy when the list could not be loaded, otherwise null. */
  errorMessage: string | null;
  refetch: () => Promise<unknown>;
}

export const useSupplierJoinRequests = (
  supplierEntityId?: string
): UseSupplierJoinRequestsResult => {
  const { data, loading, error, refetch } =
    useSuppliersPageGetRequestsToJoinQuery({
      // Deliberately unfiltered, and the query carries no `filter` at all: the
      // resolver rejects an explicit null status outright. See the query.
      variables: {
        supplierEntityId: supplierEntityId ?? '',
        first: REQUESTS_HARD_CAP,
      },
      skip: !supplierEntityId,
      fetchPolicy: 'cache-and-network',
      // errorPolicy 'all' keeps the union arms readable when the query also
      // reports transport-level errors.
      errorPolicy: 'all',
    });

  const response = data?.getRequestsToJoin;
  const requests = mapSupplierJoinRequests(response);

  return {
    requests,
    isTruncated: countJoinRequestsInWindow(response) >= REQUESTS_HARD_CAP,
    isLoading: loading,
    errorMessage: getJoinRequestsLoadError(response, Boolean(error)),
    refetch,
  };
};
